import React from "react";
import { Link } from "react-router-dom";
import { blogPosts } from "@/data/blogData";

const BlogSection: React.FC = () => {
  // Get the 4 most recent blog posts
  const recentBlogPosts = blogPosts.slice(0, 4).map(post => ({
    id: post.id,
    imageSrc: post.imageUrl,
    category: post.categories[0].toUpperCase(),
    date: post.date,
    title: post.title,
    description: post.excerpt,
    slug: post.slug
  }));

  // Arrow SVG component - horizontal arrow
  const ArrowIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="text-[rgba(192,91,42,1)] shrink-0"
    >
      <line x1="5" y1="12" x2="19" y2="12"></line>
      <polyline points="12 5 19 12 12 19"></polyline>
    </svg>
  );

  // Section header is shared between both layouts
  const SectionHeader = () => (
    <>
      <div className="z-10 flex gap-2.5 text-base text-[rgba(192,91,42,1)] font-bold leading-[29px] p-2.5">
        <img
          src="https://cdn.builder.io/api/v1/image/assets/d65dcfbabd9245a5ac68b0712fd94192/6c0d89f877af1dcc6732f857c943f6e250e8b1d1986a6a99370e80a012207347?placeholderIfAbsent=true"
          alt="Icon"
          className="aspect-[1] object-contain w-[31px] fill-[#A86747] shrink-0"
        />
        <div>Up To Date</div>
      </div>
      <h2 className="text-[#3D3E48] text-[26px] font-bold mt-[-5px]">
        News & Blog
      </h2>
      <p className="text-[#2A2A2F] text-center text-sm font-light leading-6 tracking-[0.14px] mt-[33px] max-md:max-w-full">
        Travel has helped us to understand the meaning of life and it has helped
        us become better people. Each time we travel, we see the world with new
        eyes.
      </p>
    </>
  );

  // Desktop-specific layout
  const DesktopLayout = () => (
    <div className="hidden md:block">
      <div className="flex gap-10">
        {/* Featured post (left side) */}
        <div className="w-1/2">
          <Link to={`/blog/${recentBlogPosts[0].slug}`} className="group">
            <div className="relative rounded-[10px] overflow-hidden">
              <img
                src={recentBlogPosts[0].imageSrc}
                alt={recentBlogPosts[0].title}
                className="w-full aspect-[1.6] object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent h-24"></div>
            </div>
            <div className="text-[#949494] text-xs uppercase mt-4">
              {recentBlogPosts[0].category} {recentBlogPosts[0].date}
            </div>
            <h3 className="text-[#17171B] text-2xl font-bold mt-2 group-hover:text-[#C05B2A] transition-colors">
              {recentBlogPosts[0].title}
            </h3>
            <div className="flex items-center justify-between mt-3">
              <p className="text-[#2A2A2F] text-sm pr-4 flex-1">
                {recentBlogPosts[0].description}
              </p>
              <ArrowIcon />
            </div>
          </Link>
        </div>

        {/* Other posts (right side) */}
        <div className="w-1/2 space-y-6">
          {recentBlogPosts.slice(1).map((post) => (
            <Link key={post.id} to={`/blog/${post.slug}`} className="group flex gap-4">
              <div className="w-[40%]">
                <div className="relative rounded-[10px] overflow-hidden">
                  <img
                    src={post.imageSrc}
                    alt={post.title}
                    className="w-full aspect-[1.2] object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent h-12"></div>
                </div>
              </div>
              <div className="w-[60%]">
                <div className="text-[#949494] text-xs uppercase">
                  {post.category} {post.date}
                </div>
                <h3 className="text-[#17171B] text-lg mt-2 group-hover:text-[#C05B2A] transition-colors">
                  {post.title}
                </h3>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-[#2A2A2F] text-sm pr-2 flex-1 line-clamp-2">
                    {post.description}
                  </p>
                  <ArrowIcon />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );

  // Mobile-specific layout
  const MobileLayout = () => (
    <div className="md:hidden">
      <div className="flex flex-col gap-8">
        {recentBlogPosts.map((post) => (
          <Link key={post.id} to={`/blog/${post.slug}`} className="group">
            <div className="w-full">
              {/* Image Container */}
              <div className="relative rounded-[10px] overflow-hidden">
                <img
                  src={post.imageSrc}
                  alt={post.title}
                  className="w-full aspect-[1.6] object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent h-12"></div>
              </div>
              
              {/* Text Container */}
              <div className="mt-4">
                <div className="text-[#949494] text-sm font-light leading-loose tracking-[0.14px]">
                  {post.category} {post.date}
                </div>
                <h3 className="text-[#17171B] text-xl font-bold mt-2 group-hover:text-[#C05B2A] transition-colors">
                  {post.title}
                </h3>
                <div className="flex items-center mt-3">
                  <p className="text-[#2A2A2F] text-sm font-light leading-6 tracking-[0.14px] flex-1 pr-2">
                    {post.description}
                  </p>
                  <ArrowIcon />
                </div>
              </div>
            </div>
          </Link>
        ))}
        
        {/* View All Button */}
        <Link to="/blog" className="bg-[rgba(192,91,42,1)] shadow-[0px_8px_11px_rgba(192,91,42,0.88)] text-base text-white font-extrabold text-center leading-none mt-4 px-[34px] py-[15px] rounded-[39px] self-center inline-block hover:bg-[#A34A21] transition-colors">
          VIEW ALL
        </Link>
      </div>
    </div>
  );

  return (
    <section className="bg-white self-stretch flex w-full flex-col overflow-hidden items-center mt-[45px] px-20 py-[25px] max-md:max-w-full max-md:mt-10 max-md:px-5">
      <SectionHeader />
      <div className="self-stretch w-full mt-[47px] max-md:mt-10">
        <DesktopLayout />
        <MobileLayout />
      </div>
    </section>
  );
};

export default BlogSection;
